import { environment } from "@services/environment"
import { jpegQuality } from "./constants"
import { IPdfImageUrl } from "./types"

/**
 * Cached binary asset loading for PDF generation:
 * artwork images (fullscreen -> detail fallback), org template PDFs,
 * the hardcoded back cover, attachment PDFs and the no-photo placeholder.
 *
 * iOS equivalents: HelperFunctions.downloadContent / getImage,
 * UIImage.jpeg(.medium), resizeToLimit.
 */

const bytesCache = new Map<string, Promise<Uint8Array | null>>()

/**
 * The images CDN serves bytes without CORS headers, so direct fetch() reads
 * are blocked by the browser. In development the dev server proxies the CDN
 * under /cdn-images (craco.config.js). In production the CloudFront
 * distribution must send Access-Control-Allow-Origin for the app origin —
 * until then this rewrite only helps local development.
 */
function corsSafeUrl(url: string): string {
	if (process.env.NODE_ENV === "development" && url.startsWith(environment.IMAGES_CDN)) {
		return `/cdn-images${url.slice(environment.IMAGES_CDN.length)}`
	}
	return url
}

async function fetchBytes(url: string): Promise<Uint8Array | null> {
	try {
		const res = await fetch(corsSafeUrl(url))
		if (!res.ok) {
			return null
		}
		return new Uint8Array(await res.arrayBuffer())
	} catch {
		return null
	}
}

/** Cached fetch; failed fetches resolve null (and are not cached). */
export function loadBytes(url: string): Promise<Uint8Array | null> {
	const cached = bytesCache.get(url)
	if (cached) {
		return cached
	}
	const promise = fetchBytes(url).then((bytes) => {
		if (bytes === null) {
			bytesCache.delete(url)
		}
		return bytes
	})
	bytesCache.set(url, promise)
	return promise
}

/** iOS: WorkPDFImageUrlModel — fullscreen with detail fallback. */
export async function loadArtworkImage(image: IPdfImageUrl): Promise<Uint8Array | null> {
	const primary = await loadBytes(image.url)
	if (primary) {
		return primary
	}
	return image.backupUrl ? loadBytes(image.backupUrl) : null
}

const processedImageCache = new Map<string, Promise<Uint8Array | null>>()

/**
 * Artwork image as JPEG at iOS-parity quality (0.85), cached per (url, maxSide).
 * `maxSide` caps the longest pixel side (thumbnails pass a small cap).
 */
export function loadArtworkImageJpeg(image: IPdfImageUrl, maxSide?: number): Promise<Uint8Array | null> {
	const key = `${image.url || image.backupUrl}|${maxSide ?? "full"}`
	const cached = processedImageCache.get(key)
	if (cached) {
		return cached
	}
	const promise = loadArtworkImage(image)
		.then((bytes) => (bytes ? toJpeg(bytes, maxSide) : null))
		.catch(() => null)
		.then((bytes) => {
			if (bytes === null) {
				processedImageCache.delete(key)
			}
			return bytes
		})
	processedImageCache.set(key, promise)
	return promise
}

let noPhotoCache: Promise<Uint8Array | null> | null = null

/** Bundled fallback used when an artwork has no image (iOS asset image-pdf-nophoto). */
export function loadNoPhotoImage(): Promise<Uint8Array | null> {
	if (!noPhotoCache) {
		// like the other loaders: don't cache failures, or one transient error
		// would poison the placeholder for the whole session
		noPhotoCache = fetchBytes(`${process.env.PUBLIC_URL}/pdf-assets/image-pdf-nophoto.png`).then((bytes) => {
			if (bytes === null) {
				noPhotoCache = null
			}
			return bytes
		})
	}
	return noPhotoCache
}

interface IDecodedImage {
	bitmap: HTMLImageElement
	width: number
	height: number
}

function decodeImage(bytes: Uint8Array): Promise<IDecodedImage> {
	return new Promise((resolve, reject) => {
		const blob = new Blob([bytes])
		const url = URL.createObjectURL(blob)
		const img = new Image()
		img.onload = () => {
			URL.revokeObjectURL(url)
			resolve({ bitmap: img, width: img.naturalWidth, height: img.naturalHeight })
		}
		img.onerror = () => {
			URL.revokeObjectURL(url)
			reject(new Error("Failed to decode image"))
		}
		img.src = url
	})
}

function canvasToJpegBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (!blob) {
					reject(new Error("Canvas encode failed"))
					return
				}
				blob.arrayBuffer().then((buf) => resolve(new Uint8Array(buf)))
			},
			"image/jpeg",
			jpegQuality
		)
	})
}

export interface IImageDimensions {
	width: number
	height: number
}

/** Reads intrinsic pixel dimensions without re-encoding. */
export async function getImageDimensions(bytes: Uint8Array): Promise<IImageDimensions> {
	const { width, height } = await decodeImage(bytes)
	return { width, height }
}

/**
 * Re-encode to JPEG at iOS-parity quality, optionally capping the longest
 * side (iOS resizeToLimit) to bound memory on very large originals.
 */
export async function toJpeg(bytes: Uint8Array, maxSide?: number): Promise<Uint8Array> {
	const { bitmap, width, height } = await decodeImage(bytes)
	let targetW = width
	let targetH = height
	if (maxSide && Math.max(width, height) > maxSide) {
		const scale = maxSide / Math.max(width, height)
		targetW = Math.round(width * scale)
		targetH = Math.round(height * scale)
	}
	const canvas = document.createElement("canvas")
	canvas.width = targetW
	canvas.height = targetH
	const ctx = canvas.getContext("2d")
	if (!ctx) {
		throw new Error("Canvas 2d context unavailable")
	}
	ctx.fillStyle = "#ffffff"
	ctx.fillRect(0, 0, targetW, targetH)
	ctx.drawImage(bitmap, 0, 0, targetW, targetH)
	return canvasToJpegBytes(canvas)
}

/**
 * Crop the image to the target aspect ratio (center crop) and encode as JPEG.
 * Used for aspect-FILL pages (image-fill page, WC image cover).
 */
export async function cropFill(bytes: Uint8Array, targetAspect: number, maxSide?: number): Promise<Uint8Array> {
	const { bitmap, width, height } = await decodeImage(bytes)
	const sourceAspect = width / height

	let cropW = width
	let cropH = height
	if (sourceAspect > targetAspect) {
		cropW = Math.round(height * targetAspect)
	} else {
		cropH = Math.round(width / targetAspect)
	}
	const cropX = Math.round((width - cropW) / 2)
	const cropY = Math.round((height - cropH) / 2)

	let targetW = cropW
	let targetH = cropH
	if (maxSide && Math.max(cropW, cropH) > maxSide) {
		const scale = maxSide / Math.max(cropW, cropH)
		targetW = Math.round(cropW * scale)
		targetH = Math.round(cropH * scale)
	}

	const canvas = document.createElement("canvas")
	canvas.width = targetW
	canvas.height = targetH
	const ctx = canvas.getContext("2d")
	if (!ctx) {
		throw new Error("Canvas 2d context unavailable")
	}
	ctx.fillStyle = "#ffffff"
	ctx.fillRect(0, 0, targetW, targetH)
	ctx.drawImage(bitmap, cropX, cropY, cropW, cropH, 0, 0, targetW, targetH)
	return canvasToJpegBytes(canvas)
}

/** Detects PNG vs JPEG from magic bytes so the right pdf-lib embedder is used. */
export function isPng(bytes: Uint8Array): boolean {
	return bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47
}
